import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface Inspection {
  id: string; school_id: string; school_name: string; school_code: string;
  inspection_date: string; inspection_type: string;
  compliance_score: string | null; rating: string | null;
  findings_ms: string | null; findings_en: string | null;
  report_url: string | null; action_required: boolean;
  action_deadline: string | null; created_at: string;
}

const RATING_CFG: Record<string, { label: string; color: string }> = {
  COMPLIANT:          { label: 'Patuh',          color: '#4ade80' },
  PARTIALLY_COMPLIANT:{ label: 'Sebahagian Patuh', color: '#facc15' },
  NON_COMPLIANT:      { label: 'Tidak Patuh',    color: '#f87171' },
};
const INSPECTION_TYPES = ['ROUTINE','FOLLOW_UP','COMPLAINT','RENEWAL'];

export function DPEInspectionsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [searchParams] = useSearchParams();
  const preloadSchoolId = searchParams.get('schoolId') ?? '';

  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<{ message: string; variant: ToastVariant } | null>(null);
  const [filterRating,       setFilterRating]       = useState('');
  const [filterActionOnly,   setFilterActionOnly]   = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    schoolId: preloadSchoolId, inspectionDate: new Date().toISOString().slice(0,10),
    inspectionType: 'ROUTINE', complianceScore: '', rating: '',
    findingsMs: '', findingsEn: '', reportUrl: '',
    actionRequired: false, actionDeadline: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (filterRating)     params.set('rating', filterRating);
      if (filterActionOnly) params.set('actionRequired', 'true');
      const result = await apiGet<{ data: Inspection[]; total: number }>(`/api/dpe/inspections?${params}`);
      setInspections(result.data); setTotal(result.total); setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan pemeriksaan.');
    } finally { setLoading(false); }
  }, [filterRating, filterActionOnly]);

  useEffect(() => { void load(1); }, [load]);

  function validateForm(): string | null {
    if (!form.schoolId.trim())       return 'ID sekolah diperlukan. / School ID required.';
    if (!form.inspectionDate)        return 'Tarikh pemeriksaan diperlukan. / Inspection date required.';
    if (form.complianceScore && (isNaN(parseFloat(form.complianceScore)) || parseFloat(form.complianceScore) < 0 || parseFloat(form.complianceScore) > 100))
      return 'Skor pematuhan mesti 0–100. / Compliance score must be 0–100.';
    if (form.actionRequired && !form.actionDeadline)
      return 'Tarikh tindakan diperlukan jika tindakan dikehendaki. / Action deadline required.';
    if (form.reportUrl && form.reportUrl.trim()) {
      try { new URL(form.reportUrl.trim()); } catch { return 'URL laporan tidak sah. / Invalid report URL.'; }
    }
    return null;
  }

  async function handleSave() {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);
    try {
      await apiPost('/api/dpe/inspections', {
        schoolId: form.schoolId.trim(),
        inspectionDate: form.inspectionDate,
        inspectionType: form.inspectionType,
        complianceScore: form.complianceScore ? parseFloat(form.complianceScore) : undefined,
        rating: form.rating || undefined,
        findingsMs: form.findingsMs.trim() || undefined,
        findingsEn: form.findingsEn.trim() || undefined,
        reportUrl: form.reportUrl.trim() || undefined,
        actionRequired: form.actionRequired,
        actionDeadline: form.actionDeadline || undefined,
      });
      setToast({ variant: 'success', message: 'Pemeriksaan berjaya direkodkan. / Inspection recorded.' });
      setShowForm(false);
      void load(1);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal menyimpan. / Failed to save.');
    } finally { setSaving(false); }
  }

  const totalPages = Math.ceil(total / 25) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/dpe" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{E ? '← DPE' : '← DPE'}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                {E ? '🔍 Private School Inspections' : '🔍 Pemeriksaan Sekolah Swasta'}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Private School Inspections — {total} rekod
              </p>
            </div>
            {isAdmin && !showForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setFormError(null); }}>
                + Rekod Pemeriksaan
              </Button>
            )}
          </div>

          {/* Record form */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                Rekod Pemeriksaan Baharu / Record New Inspection
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <div style={{ flex: '2 1 180px' }}>
                    <label style={labelSt}>ID Sekolah *</label>
                    <input type="text" value={form.schoolId} onChange={(e) => setForm((f) => ({ ...f, schoolId: e.target.value }))} disabled={saving} style={inputSt()} />
                  </div>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelSt}>Tarikh *</label>
                    <input type="date" value={form.inspectionDate} onChange={(e) => setForm((f) => ({ ...f, inspectionDate: e.target.value }))} disabled={saving} style={inputSt()} />
                  </div>
                  <div style={{ flex: '1 1 120px' }}>
                    <label style={labelSt}>Jenis</label>
                    <select value={form.inspectionType} onChange={(e) => setForm((f) => ({ ...f, inspectionType: e.target.value }))} disabled={saving} style={inputSt()}>
                      {INSPECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 120px' }}>
                    <label style={labelSt}>Skor Pematuhan (0–100)</label>
                    <input type="number" value={form.complianceScore} onChange={(e) => { const v = e.target.value; if (!v || (parseFloat(v) >= 0 && parseFloat(v) <= 100)) setForm((f) => ({ ...f, complianceScore: v })); }} disabled={saving} min={0} max={100} step={0.01} style={inputSt()} />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={labelSt}>Penilaian / Rating</label>
                    <select value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} disabled={saving} style={inputSt()}>
                      <option value="">-- Pilih --</option>
                      {Object.entries(RATING_CFG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '2 1 200px' }}>
                    <label style={labelSt}>URL Laporan</label>
                    <input type="url" value={form.reportUrl} onChange={(e) => setForm((f) => ({ ...f, reportUrl: e.target.value }))} placeholder="https://…" disabled={saving} style={inputSt()} maxLength={500} />
                  </div>
                </div>

                <div>
                  <label style={labelSt}>Dapatan (BM)</label>
                  <textarea value={form.findingsMs} onChange={(e) => setForm((f) => ({ ...f, findingsMs: e.target.value }))} rows={3} disabled={saving} style={{ ...inputSt(), resize: 'vertical' }} />
                </div>
                <div>
                  <label style={labelSt}>Findings (EN)</label>
                  <textarea value={form.findingsEn} onChange={(e) => setForm((f) => ({ ...f, findingsEn: e.target.value }))} rows={3} disabled={saving} style={{ ...inputSt(), resize: 'vertical' }} />
                </div>

                <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, cursor: saving ? 'not-allowed' : 'pointer', fontSize: typography.fontSize.sm, color: colors.neutral.white }}>
                    <input type="checkbox" checked={form.actionRequired} onChange={(e) => setForm((f) => ({ ...f, actionRequired: e.target.checked, actionDeadline: e.target.checked ? f.actionDeadline : '' }))} disabled={saving} />
                    Tindakan Diperlukan / Action Required
                  </label>
                  {form.actionRequired && (
                    <div>
                      <label style={labelSt}>Tarikh Tindakan *</label>
                      <input type="date" value={form.actionDeadline} min={form.inspectionDate || undefined} onChange={(e) => setForm((f) => ({ ...f, actionDeadline: e.target.value }))} disabled={saving} style={inputSt()} />
                    </div>
                  )}
                </div>
              </div>

              {formError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving}>
                  {saving ? 'Menyimpan…' : 'Simpan / Save'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setFormError(null); }} disabled={saving}>Batal</Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelSt}>Penilaian / Rating</label>
                <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)} disabled={loading} style={inputSt()}>
                  <option value="">Semua</option>
                  {Object.entries(RATING_CFG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, cursor: 'pointer', fontSize: typography.fontSize.sm, color: colors.neutral.white, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={filterActionOnly} onChange={(e) => setFilterActionOnly(e.target.checked)} />
                Tindakan diperlukan sahaja
              </label>
              <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Tapis / Filter'}
              </Button>
            </div>
          </div>

          {error && <div style={{ backgroundColor:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:borderRadius.lg, padding:spacing.lg, marginBottom:spacing.lg }}>
            <p style={{ margin:0, color:'#f87171', fontSize:typography.fontSize.sm }}>{error}</p>
            <Button variant="ghost" size="sm" onClick={() => void load(page)} style={{ marginTop:spacing.xs }}>Cuba Lagi</Button>
          </div>}

          {loading && inspections.length === 0 && <div style={{ display:'flex', justifyContent:'center', padding:spacing.xl }}><Spinner size="lg" /></div>}
          {!loading && !error && inspections.length === 0 && (
            <div style={{ backgroundColor:colors.neutral.charcoal, borderRadius:borderRadius.xl, padding:spacing.xl, textAlign:'center' }}>
              <div style={{ fontSize:'48px', marginBottom:spacing.sm }}>🔍</div>
              <p style={{ margin:0, fontWeight:typography.fontWeight.semibold, color:colors.neutral.white }}>Tiada Rekod Pemeriksaan</p>
              <p style={{ margin:`${spacing.xs} 0 0`, color:colors.neutral.gray400, fontSize:typography.fontSize.sm }}>No inspection records found.</p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {inspections.map((ins) => {
              const rc = ins.rating ? RATING_CFG[ins.rating] ?? { label: ins.rating, color: '#9ca3af' } : null;
              const actionOverdue = ins.action_required && ins.action_deadline && new Date(ins.action_deadline) < new Date();

              return (
                <div key={ins.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${actionOverdue ? 'rgba(248,113,113,0.4)' : ins.action_required ? 'rgba(250,204,21,0.3)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        {rc && <Pill label={rc.label} color={rc.color} />}
                        <Pill label={ins.inspection_type} color="#60a5fa" />
                        {ins.action_required && (
                          <Pill label={actionOverdue ? '⚠ Tindakan Tertunggak' : '⚡ Tindakan Diperlukan'} color={actionOverdue ? '#f87171' : '#facc15'} />
                        )}
                        {ins.compliance_score !== null && (
                          <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            Skor: {parseFloat(ins.compliance_score).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{ins.school_name}</p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {ins.school_code} · {new Date(ins.inspection_date).toLocaleDateString('ms-BN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      {ins.findings_ms && (
                        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, lineHeight: '1.5' }}>
                          {ins.findings_ms.length > 160 ? `${ins.findings_ms.slice(0, 160)}…` : ins.findings_ms}
                        </p>
                      )}
                      {ins.action_deadline && (
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: actionOverdue ? '#f87171' : '#facc15' }}>
                          {E ? 'Action before:' : 'Tindakan sebelum:'} {new Date(ins.action_deadline).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: spacing.xs, flexShrink: 0 }}>
                      {ins.report_url && (
                        <a href={ins.report_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm">📄 Laporan</Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {Math.ceil(total / 25) > 1 && (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:spacing.sm, marginTop:spacing.xl }}>
              <Button variant="ghost" size="sm" disabled={page<=1||loading} onClick={() => void load(page-1)}>← Sebelum</Button>
              <span style={{ fontSize:typography.fontSize.sm, color:colors.neutral.gray400 }}>{page}/{Math.ceil(total/25)}</span>
              <Button variant="ghost" size="sm" disabled={page>=Math.ceil(total/25)||loading} onClick={() => void load(page+1)}>Seterusnya →</Button>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display:'inline-block', padding:`1px ${spacing.xs}`, backgroundColor:`${color}22`, color, borderRadius:borderRadius.full, fontSize:typography.fontSize.xs, fontWeight:typography.fontWeight.bold }}>{label}</span>;
}
const labelSt: React.CSSProperties = { display:'block', fontSize:typography.fontSize.xs, color:colors.neutral.gray400, marginBottom:spacing.xs };
function inputSt(): React.CSSProperties {
  return { width:'100%', backgroundColor:colors.neutral.black, border:`1px solid ${colors.neutral.gray800}`, borderRadius:borderRadius.md, color:colors.neutral.white, padding:`${spacing.xs} ${spacing.sm}`, fontSize:typography.fontSize.sm, outline:'none', boxSizing:'border-box', fontFamily:typography.fontFamily.body };
}
